import { Box, useMediaQuery, useTheme } from '@mui/material';
import FlexBox from 'components/shared/FlexBox';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from 'utils/numberFormatter';
import useQuotation from '../../../hooks/quotations/useQuotation';
import useQuotationSummarySWR from '../../../hooks/quotations/useQuotationSummarySWR';
import { QuotationAccordionProps } from '../../../models/quotations/Quotation';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { TableLoadingLayout } from '../../layouts/LoadingLayouts/TableLoadingLayout';
import { QuotationSummaryProducts } from '../../shared/quotation/quotation-products/QuotationSummaryProducts';
import { ErrorTypography } from '../../styled/ErrorTypography';
import { QuotationNextButton } from '../shared/QuotationNextButton';
import { QuotationSummaryTitle } from '../shared/QuotationSummaryTitle';
import { QuotationSummaryDetails } from './components/QuotationSummaryDetails';
import { QuotationSummaryPDF } from './components/QuotationSummaryPDF';

export const QuotationSummary: React.FC<QuotationAccordionProps> = (props) => {
    const { quoteData } = props;
    const quotation = useQuotation();
    const theme = useTheme();
    const small = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.SUMMARY',
    });
    const navigate = useNavigate();
    const { quote, isLoading, isError, forceRefresh } = useQuotationSummarySWR(
        quoteData.id.toString()
    );

    const [comment, setComment] = useState<string>('');
    const [useLogo, setUseLogo] = useState<boolean>(false);

    useEffect(() => {
        forceRefresh().then((res) => {
            setComment(res.comment_2 ? res.comment_2 : '');
            setUseLogo(res.use_logo);
        });
    }, []);

    const save = () => {
        quotation
            .saveSummary(
                {
                    comment_2: comment,
                    use_logo: useLogo,
                },
                quoteData.id
            )
            .then(() => {
                SnackbarUtils.success(
                    t('SUMMARY_SAVED_SUCCESSFULLY').toString()
                );
                forceRefresh();
                //handleNext(index);
                navigate('/quotations');
            });
    };

    return (
        <>
            {isError && <ErrorTypography errorCode={'GLOBAL_ERROR'} />}
            {isLoading && <TableLoadingLayout />}
            {!isLoading && (
                <>
                    <div>
                        <QuotationSummaryDetails quoteData={quote} />

                        <div>
                            <FlexBox>
                                <Box paddingY="24px">
                                    {t('TOTAL_MARGIN')}:{' '}
                                    <b>
                                        {formatPrice(
                                            quote.items.reduce((sum, item) => {
                                                sum +=
                                                    item.margin * item.quantity;
                                                return sum;
                                            }, 0)
                                        )}{' '}
                                        Ft
                                    </b>
                                </Box>
                            </FlexBox>
                        </div>

                        <div>
                            <QuotationSummaryTitle>
                                {t('PRODUCTS.TITLE')}
                            </QuotationSummaryTitle>

                            <QuotationSummaryProducts quoteData={quote} />
                        </div>

                        <QuotationSummaryPDF
                            quoteData={quote}
                            changeNote={setComment}
                            changeLogo={setUseLogo}
                        />
                    </div>
                    <div>
                        <QuotationNextButton fullWidth={small} finalSave onClick={save} />
                    </div>
                </>
            )}
        </>
    );
};
